"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.applyColumnFilters = applyColumnFilters;
exports.applySort = applySort;
const operator_handlers_1 = require("./operator-handlers");
const types_1 = require("./types");
function applyColumnFilters(items, filters, schema, accessors, logicOperator = 'and') {
    if (filters.length === 0) {
        return items;
    }
    const ignored = new Set(schema.ignoredFields ?? []);
    const predicates = [];
    for (const filter of filters) {
        if (ignored.has(filter.field)) {
            continue;
        }
        if (!types_1.VALID_FIELD_NAME_PATTERN.test(filter.field)) {
            continue;
        }
        if (!Object.hasOwn(schema.fields, filter.field)) {
            continue;
        }
        const fieldDef = schema.fields[filter.field];
        if (fieldDef.allowedOperators && !fieldDef.allowedOperators.includes(filter.operator)) {
            continue;
        }
        const accessor = accessors[filter.field];
        if (!accessor) {
            continue;
        }
        const predicate = buildPredicate(fieldDef, filter.operator, filter.value, accessor);
        if (predicate) {
            predicates.push(predicate);
        }
    }
    if (predicates.length === 0) {
        return items;
    }
    if (logicOperator === 'or') {
        return items.filter((item) => predicates.some((p) => p(item)));
    }
    return items.filter((item) => predicates.every((p) => p(item)));
}
function applySort(items, sort, schema, accessors) {
    if (!sort || sort.length === 0) {
        return items;
    }
    const validSorts = [];
    for (const s of sort) {
        if (!types_1.VALID_FIELD_NAME_PATTERN.test(s.field)) {
            continue;
        }
        if (!Object.hasOwn(schema.fields, s.field)) {
            continue;
        }
        const fieldDef = schema.fields[s.field];
        if (fieldDef.options.type === types_1.FieldType.Relation || fieldDef.options.type === types_1.FieldType.Custom) {
            continue;
        }
        const accessor = accessors[s.field];
        if (!accessor) {
            continue;
        }
        const direction = s.direction === 'desc' ? -1 : 1;
        validSorts.push({ accessor, direction });
    }
    if (validSorts.length === 0) {
        return items;
    }
    return [...items].sort((a, b) => {
        for (const { accessor, direction } of validSorts) {
            const av = accessor(a);
            const bv = accessor(b);
            const cmp = compareSortValues(av, bv);
            if (cmp !== 0) {
                return cmp * direction;
            }
        }
        return 0;
    });
}
function buildPredicate(fieldDef, operator, rawValue, accessor) {
    const { options } = fieldDef;
    try {
        switch (options.type) {
            case types_1.FieldType.String:
                return buildStringPredicate(operator, rawValue, accessor, options.nonNullable);
            case types_1.FieldType.Boolean:
                return buildBooleanPredicate(operator, rawValue, accessor);
            case types_1.FieldType.Number:
                return buildNumberPredicate(operator, rawValue, accessor);
            case types_1.FieldType.Date:
                return buildDatePredicate(operator, rawValue, accessor);
            case types_1.FieldType.Relation:
            case types_1.FieldType.Custom:
                return null;
        }
    }
    catch {
        return null;
    }
}
function buildStringPredicate(operator, rawValue, accessor, nonNullable) {
    switch (operator) {
        case 'contains': {
            if (rawValue === undefined) {
                return null;
            }
            const lower = rawValue.toLowerCase();
            return (item) => someString(accessor(item), (s) => s.toLowerCase().includes(lower));
        }
        case 'doesNotContain': {
            if (rawValue === undefined) {
                return null;
            }
            const lower = rawValue.toLowerCase();
            return (item) => !someString(accessor(item), (s) => s.toLowerCase().includes(lower));
        }
        case 'equals':
        case 'is': {
            if (rawValue === undefined) {
                return null;
            }
            return (item) => someString(accessor(item), (s) => s === rawValue);
        }
        case 'doesNotEqual':
        case 'not': {
            if (rawValue === undefined) {
                return null;
            }
            return (item) => !someString(accessor(item), (s) => s === rawValue);
        }
        case 'startsWith': {
            if (rawValue === undefined) {
                return null;
            }
            const lower = rawValue.toLowerCase();
            return (item) => someString(accessor(item), (s) => s.toLowerCase().startsWith(lower));
        }
        case 'endsWith': {
            if (rawValue === undefined) {
                return null;
            }
            const lower = rawValue.toLowerCase();
            return (item) => someString(accessor(item), (s) => s.toLowerCase().endsWith(lower));
        }
        case 'isEmpty':
            return (item) => {
                const v = accessor(item);
                if (v === undefined || v === null) {
                    return !nonNullable;
                }
                if (Array.isArray(v)) {
                    return v.length === 0 || v.every((s) => s === '');
                }
                return v === '';
            };
        case 'isNotEmpty':
            return (item) => {
                const v = accessor(item);
                if (v === undefined || v === null) {
                    return false;
                }
                if (Array.isArray(v)) {
                    return v.some((s) => s !== '' && s !== null && s !== undefined);
                }
                return v !== '';
            };
        case 'isAnyOf': {
            const values = (0, operator_handlers_1.parseIsAnyOfValues)(rawValue);
            const set = new Set(values);
            return (item) => someString(accessor(item), (s) => set.has(s));
        }
        case 'isNoneOf': {
            const values = (0, operator_handlers_1.parseIsAnyOfValues)(rawValue);
            const set = new Set(values);
            return (item) => !someString(accessor(item), (s) => set.has(s));
        }
        default:
            return null;
    }
}
function buildBooleanPredicate(operator, rawValue, accessor) {
    const known = (item) => {
        const value = accessor(item);
        if (typeof value === 'boolean') {
            return value;
        }
        if (value === 'true' || value === 'false') {
            return value === 'true';
        }
        return null;
    };
    switch (operator) {
        case 'equals':
        case 'is': {
            const target = rawValue === 'true';
            return (item) => known(item) === target;
        }
        case 'doesNotEqual':
        case 'not': {
            const target = rawValue === 'true';
            return (item) => {
                const value = known(item);
                return value !== null && value !== target;
            };
        }
        default:
            return null;
    }
}
function buildNumberPredicate(operator, rawValue, accessor) {
    switch (operator) {
        case 'equals':
        case 'is': {
            const n = (0, operator_handlers_1.parseNumberValue)(rawValue);
            return (item) => someNumber(accessor(item), (x) => x === n);
        }
        case 'doesNotEqual':
        case 'not': {
            const n = (0, operator_handlers_1.parseNumberValue)(rawValue);
            return (item) => !someNumber(accessor(item), (x) => x === n);
        }
        case 'greaterThan': {
            const n = (0, operator_handlers_1.parseNumberValue)(rawValue);
            return (item) => someNumber(accessor(item), (x) => x > n);
        }
        case 'greaterThanOrEqual': {
            const n = (0, operator_handlers_1.parseNumberValue)(rawValue);
            return (item) => someNumber(accessor(item), (x) => x >= n);
        }
        case 'lessThan': {
            const n = (0, operator_handlers_1.parseNumberValue)(rawValue);
            return (item) => someNumber(accessor(item), (x) => x < n);
        }
        case 'lessThanOrEqual': {
            const n = (0, operator_handlers_1.parseNumberValue)(rawValue);
            return (item) => someNumber(accessor(item), (x) => x <= n);
        }
        case 'isEmpty':
            return (item) => {
                const v = accessor(item);
                return v === null || v === undefined;
            };
        case 'isNotEmpty':
            return (item) => {
                const v = accessor(item);
                return v !== null && v !== undefined;
            };
        case 'isAnyOf': {
            const raw = (0, operator_handlers_1.parseIsAnyOfValues)(rawValue);
            const numbers = raw.map((item) => {
                const n = Number(item);
                if (!Number.isFinite(n)) {
                    throw new Error(`Numeric isAnyOf received non-numeric entry: ${item}`);
                }
                return n;
            });
            const set = new Set(numbers);
            return (item) => someNumber(accessor(item), (x) => set.has(x));
        }
        case 'isNoneOf': {
            const raw = (0, operator_handlers_1.parseIsAnyOfValues)(rawValue);
            const numbers = raw.map((item) => {
                const n = Number(item);
                if (!Number.isFinite(n)) {
                    throw new Error(`Numeric isNoneOf received non-numeric entry: ${item}`);
                }
                return n;
            });
            const set = new Set(numbers);
            return (item) => !someNumber(accessor(item), (x) => set.has(x));
        }
        default:
            return null;
    }
}
function buildDatePredicate(operator, rawValue, accessor) {
    switch (operator) {
        case 'equals':
        case 'is': {
            const t = (0, operator_handlers_1.parseDateValue)(rawValue).getTime();
            return (item) => someDate(accessor(item), (x) => x === t);
        }
        case 'doesNotEqual':
        case 'not': {
            const t = (0, operator_handlers_1.parseDateValue)(rawValue).getTime();
            return (item) => !someDate(accessor(item), (x) => x === t);
        }
        case 'greaterThan': {
            const t = (0, operator_handlers_1.parseDateValue)(rawValue).getTime();
            return (item) => someDate(accessor(item), (x) => x > t);
        }
        case 'greaterThanOrEqual': {
            const t = (0, operator_handlers_1.parseDateValue)(rawValue).getTime();
            return (item) => someDate(accessor(item), (x) => x >= t);
        }
        case 'lessThan': {
            const t = (0, operator_handlers_1.parseDateValue)(rawValue).getTime();
            return (item) => someDate(accessor(item), (x) => x < t);
        }
        case 'lessThanOrEqual': {
            const t = (0, operator_handlers_1.parseDateValue)(rawValue).getTime();
            return (item) => someDate(accessor(item), (x) => x <= t);
        }
        case 'isEmpty':
            return (item) => {
                const v = accessor(item);
                return v === null || v === undefined;
            };
        case 'isNotEmpty':
            return (item) => {
                const v = accessor(item);
                return v !== null && v !== undefined;
            };
        case 'isAnyOf': {
            const times = new Set((0, operator_handlers_1.parseIsAnyOfValues)(rawValue).map((v) => (0, operator_handlers_1.parseDateValue)(v).getTime()));
            return (item) => someDate(accessor(item), (x) => times.has(x));
        }
        case 'isNoneOf': {
            const times = new Set((0, operator_handlers_1.parseIsAnyOfValues)(rawValue).map((v) => (0, operator_handlers_1.parseDateValue)(v).getTime()));
            return (item) => !someDate(accessor(item), (x) => times.has(x));
        }
        default:
            return null;
    }
}
function someString(value, predicate) {
    if (value === null || value === undefined) {
        return false;
    }
    if (Array.isArray(value)) {
        return value.some((v) => typeof v === 'string' && predicate(v));
    }
    if (typeof value !== 'string') {
        return false;
    }
    return predicate(value);
}
function someNumber(value, predicate) {
    if (value === null || value === undefined) {
        return false;
    }
    if (Array.isArray(value)) {
        return value.some((v) => typeof v === 'number' && predicate(v));
    }
    if (typeof value !== 'number') {
        return false;
    }
    return predicate(value);
}
function someDate(value, predicate) {
    if (value === null || value === undefined) {
        return false;
    }
    const apply = (v) => {
        const ms = typeof v === 'number' ? v
            : /^-?\d+$/.test(v.trim()) ? Number(v)
                : Date.parse(v);
        return !Number.isNaN(ms) && predicate(ms);
    };
    if (Array.isArray(value)) {
        return value.some((v) => (typeof v === 'string' || typeof v === 'number') && apply(v));
    }
    if (typeof value !== 'string' && typeof value !== 'number') {
        return false;
    }
    return apply(value);
}
function compareSortValues(a, b) {
    const aEmpty = a === null || a === undefined;
    const bEmpty = b === null || b === undefined;
    if (aEmpty && bEmpty) {
        return 0;
    }
    if (aEmpty) {
        return 1;
    }
    if (bEmpty) {
        return -1;
    }
    const av = Array.isArray(a) ? (a[0] ?? null) : a;
    const bv = Array.isArray(b) ? (b[0] ?? null) : b;
    if (av === null || av === undefined) {
        return bv === null || bv === undefined ? 0 : 1;
    }
    if (bv === null || bv === undefined) {
        return -1;
    }
    if (typeof av === 'number' && typeof bv === 'number') {
        return (av < bv ? -1
            : av > bv ? 1
                : 0);
    }
    if (typeof av === 'boolean' && typeof bv === 'boolean') {
        return (av === bv ? 0
            : av ? 1
                : -1);
    }
    const as = String(av);
    const bs = String(bv);
    return (as < bs ? -1
        : as > bs ? 1
            : 0);
}
//# sourceMappingURL=in-memory-executor.js.map